import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Input, InputNumber, DatePicker, Select, Button } from 'antd';
import { toast } from 'react-toastify';
import Icons from '@/components/third-party/Icons';
import PageModal from '@/components/PageModal';
import dayjs from 'dayjs';
import { update_production_demand } from '@/utils/api';

// ==============================|| EDIT PRODUCTION DEMAND MODAL ||============================== //

// Product/Recipe are shown but never editable here: create_production_demand schedules a
// real master batch (process/operation chain) off the recipe at creation time, so changing
// the recipe after the fact would mean re-scheduling that batch from scratch, not a field
// edit. Only what a planner would realistically revise before approval — quantity, dates,
// priority — is editable. See the "Backend needed" note for what update_production_demand
// still needs on the server.
const EditDemandModal = ({ open, onClose, onUpdated, demand }) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && demand) {
      form.setFieldsValue({
        product_name: demand.product_name,
        recipe_name: demand.recipe_name,
        required_quantity: demand.required_quantity,
        dates: [dayjs(demand.start_date), dayjs(demand.end_date)],
        priority: demand.priority || 'MEDIUM',
      });
    }
  }, [open, demand, form]);

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const handleSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    setSubmitting(true);
    update_production_demand(
      demand.demand_id,
      {
        required_quantity: values.required_quantity,
        start_date: values.dates[0].format('YYYY-MM-DD'),
        end_date: values.dates[1].format('YYYY-MM-DD'),
        priority: values.priority,
      },
      (data) => {
        setSubmitting(false);
        if (data?.success) {
          toast.success('Demand updated');
          onUpdated?.();
          onClose();
        } else {
          toast.error(data?.message || 'Failed to update demand');
        }
      },
      { onError: () => setSubmitting(false) }
    );
  };

  return (
    <PageModal
      open={open}
      onCancel={handleCancel}
      icon={<Icons.Pencil size={22} />}
      title={`Edit Demand — ${demand?.batch_number || ''}`}
      description="A demand can be edited while it is a draft or rejected."
      width={640}
      destroyOnHidden
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={handleCancel}>Cancel</Button>
          <Button type="primary" loading={submitting} onClick={handleSubmit}>Save Changes</Button>
        </div>
      }
    >
      <Form form={form} layout="vertical">
        <Form.Item name="product_name" label="Product">
          <Input disabled />
        </Form.Item>

        <Form.Item name="recipe_name" label="Recipe">
          <Input disabled />
        </Form.Item>

        <Form.Item
          name="required_quantity"
          label="Required Quantity (Batch Size)"
          rules={[
            { required: true, message: 'Required quantity is required' },
            { type: 'number', min: 0.01, message: 'Must be greater than 0' },
          ]}
        >
          <InputNumber style={{ width: '100%' }} min={0.01} placeholder="Enter quantity" />
        </Form.Item>

        <Form.Item
          name="dates"
          label="Demand Window (Start - End Date)"
          rules={[{ required: true, message: 'Start and end date are required' }]}
        >
          <DatePicker.RangePicker
            style={{ width: '100%' }}
            disabledDate={(current, { from } = {}) => {
              if (current && current < dayjs().startOf('day')) return true;
              if (from) return current && current < from.startOf('day');
              return false;
            }}
          />
        </Form.Item>

        <Form.Item name="priority" label="Priority">
          <Select>
            <Select.Option value="HIGH">High</Select.Option>
            <Select.Option value="MEDIUM">Medium</Select.Option>
            <Select.Option value="LOW">Low</Select.Option>
          </Select>
        </Form.Item>

      </Form>
    </PageModal>
  );
};

EditDemandModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpdated: PropTypes.func,
  demand: PropTypes.object,
};

export default EditDemandModal;
